import { globalAppThemeLocalStorage } from 'components/GlobalState/globalAppThemeLocalStorage';
import { type FC, useState } from 'react';

import { FeatureFlagDialog } from './FeatureFlagDialog';
import { ResizableQueryDevtoolsPanel } from './ResizableQueryDevtoolsPanel';
import {
  getLocalFeatureFlags,
  toggleLocalFeatureFlag,
} from './featureFlagLocalStorage';

type ToolbarMenuItem = {
  label: string;
  icon: string;
  onClick: () => void;
  active?: boolean;
};

/**
 * Development toolbar providing quick access to dev tools:
 * - TanStack Query DevTools
 * - React Hook Form DevTools
 * - Feature Flags Dialog
 * - Dark Mode
 */
export const DevToolbar: FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [queryDevToolsOpen, setQueryDevToolsOpen] = useState(false);
  const [featureFlagDialogOpen, setFeatureFlagDialogOpen] = useState(false);

  const darkMode = globalAppThemeLocalStorage.get() === 'dark';

  // Read directly from local storage - API call may not be finished during DevToolbar init
  const localFlags = getLocalFeatureFlags();
  const hookFormDevToolsEnabled =
    localFlags.ENABLE_HOOK_FORM_DEV_TOOLS === true;

  const handleToggleHookFormDevTools = () => {
    // Toggle: if currently enabled (either by server or local override), disable it; otherwise enable it
    const newValue = !hookFormDevToolsEnabled;
    toggleLocalFeatureFlag('ENABLE_HOOK_FORM_DEV_TOOLS', newValue);

    setMenuOpen(false);

    // Prompt for reload since feature flags are loaded once on startup
    if (
      window.confirm(
        `React Hook Form DevTools ${newValue ? 'enabled' : 'disabled'}.\n\nBe sure that you have <FormDevTool control={control} /> in your <form>\n\nReload page now to apply?`,
      )
    ) {
      window.location.reload();
    }
  };

  const handleToggleDarkMode = () => {
    const newTheme = darkMode ? 'light' : 'dark';
    globalAppThemeLocalStorage.set(newTheme);
    setMenuOpen(false);

    if (
      window.confirm(
        `Theme switched to ${newTheme} mode.\n\nReload page now to apply?`,
      )
    ) {
      window.location.reload();
    }
  };

  const menuItems: ToolbarMenuItem[] = [
    {
      label: 'TanStack Query DevTools',
      icon: '🔍',
      onClick: () => {
        setQueryDevToolsOpen((prev) => !prev);
        setMenuOpen(false);
      },
      active: queryDevToolsOpen,
    },
    {
      label: 'React Hook Form DevTools',
      icon: '📝',
      onClick: handleToggleHookFormDevTools,
      active: hookFormDevToolsEnabled,
    },
    {
      label: 'Feature Flags',
      icon: '🚩',
      onClick: () => {
        setFeatureFlagDialogOpen(true);
        setMenuOpen(false);
      },
    },
    {
      label: 'Dark Mode',
      icon: darkMode ? '☀️' : '🌙',
      onClick: handleToggleDarkMode,
      active: darkMode,
    },
  ];

  return (
    <>
      {/* Floating toolbar button */}
      <div className='fixed bottom-4 right-4 z-[9999]'>
        {/* Menu dropdown */}
        {menuOpen && (
          <div className='absolute bottom-14 right-0 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 min-w-56 overflow-hidden'>
            <div className='px-3 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700'>
              <div className='text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide'>
                Dev Tools
              </div>
              <div className='text-xs text-gray-400 dark:text-gray-400'>
                Green border - leave dialog is not active
              </div>
            </div>
            {menuItems.map((item) => (
              <button
                key={item.label}
                type='button'
                onClick={item.onClick}
                className={`
                  w-full px-3 py-2 text-left text-sm flex items-center gap-2 text-gray-900 dark:text-gray-100
                  hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors
                  ${item.active ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'}
                `}
              >
                <span className='text-base'>{item.icon}</span>
                <span>{item.label}</span>
                {item.active && (
                  <span className='ml-auto text-xs bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-300 px-1.5 py-0.5 rounded'>
                    ON
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Main toggle button */}
        <button
          type='button'
          onClick={() => setMenuOpen((prev) => !prev)}
          className={`
            w-12 h-12 rounded-full shadow-lg flex items-center justify-center
            text-xl transition-all duration-200 hover:scale-110
            ${menuOpen ? 'bg-blue-600 text-white rotate-45' : 'bg-gray-800 text-white hover:bg-gray-700 bg-opacity-40'}
          `}
          title='Dev Tools'
        >
          {menuOpen ? '✕' : '⚙️'}
        </button>
      </div>

      {/* React Query DevTools Panel - resizable, with body padding for scrollability */}
      {queryDevToolsOpen && (
        <ResizableQueryDevtoolsPanel
          onClose={() => setQueryDevToolsOpen(false)}
        />
      )}

      {/* Feature Flag Dialog */}
      {featureFlagDialogOpen && (
        <FeatureFlagDialog onClose={() => setFeatureFlagDialogOpen(false)} />
      )}
    </>
  );
};
